import React, { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart
} from 'recharts';
import { motion } from 'framer-motion';
import GaugeChart from 'react-gauge-chart';
import {
  ChartBarIcon,
  EyeIcon,
  CalendarIcon,
  TrophyIcon,
  SparklesIcon,
  CpuChipIcon,
  FireIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

interface AdvancedChartsProps {
  data: {
    analysis: {
      monthlyData: any[];
      weeklyData: any[];
      objectives: any[];
      kpis: {
        totalRevenueAnnualPrevious: string;
        totalFreqAnnualPrevious: string;
        avgPMAnnualPrevious: string;
        totalRevenueToDatePrevious: string;
        totalFreqToDatePrevious: string;
        avgPMToDatePrevious: string;
        avgMonthlyToDatePrevious: string;
        totalRevenueAnnualCurrent: string;
        totalFreqAnnualCurrent: string;
        avgPMAnnualCurrent: string;
        totalRevenueToDateCurrent: string;
        totalFreqToDateCurrent: string;
        avgPMToDateCurrent: string;
        avgMonthlyToDateCurrent: string;
        totalObjectivesToDate: string;
        totalObjectivesAnnual: string;
        objectiveRealizationToDate: string;
        objectiveRealizationAnnual: string;
        totalCAObjectivesAnnual: string;
        totalFreqObjectivesAnnual: string;
        totalPMObjectivesAnnual: string;
        revenueGrowthToDate: string;
        freqGrowthToDate: string;
        pmGrowthToDate: string;
        revenueGrowthAnnual: string;
        freqGrowthAnnual: string;
        pmGrowthAnnual: string;
        yearPrevious: number;
        yearCurrent: number;
        currentMonth: number;
        currentMonthName: string;
        yearEndPrediction?: {
          realistic: number;
          optimistic: number;
          pessimistic: number;
          confidence: number;
          realisticVsObjective: string;
          methodology: string;
          probabilities: {
            optimistic: number;
            realistic: number;
            pessimistic: number;
          };
        };
      };
    };
  };
}

const AdvancedCharts: React.FC<AdvancedChartsProps> = ({ data }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'detailed' | 'predictive' | 'weekly'>('overview');
  const [selectedQuarter, setSelectedQuarter] = useState<number>(0); // 0 = tous, 1-4 = trimestres
  
  const { analysis } = data;

  // Couleurs pour les graphiques
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'];
  const PERFORMANCE_COLORS = {
    excellent: '#10B981',
    good: '#F59E0B', 
    poor: '#EF4444'
  };

  // Vérifier si des données hebdomadaires sont disponibles
  const hasWeeklyData = analysis.weeklyData && analysis.weeklyData.length > 0;

  // Fonction helper pour calculer la corrélation
  const calculateCorrelation = (x: number[], y: number[]) => {
    if (x.length !== y.length || x.length === 0) return 0.5;
    
    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);
    const sumY2 = y.reduce((sum, yi) => sum + yi * yi, 0);
    
    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
    
    return denominator === 0 ? 0.5 : Math.abs(numerator / denominator);
  };

  // Données hebdomadaires pour les graphiques
  const weeklyChartData = useMemo(() => {
    if (!hasWeeklyData) return [];
    
    // Organiser les données par semaine
    const weeklyMap = new Map();
    
    analysis.weeklyData.forEach(item => {
      const key = `${item.year}_${item.weekNumber}`;
      if (!weeklyMap.has(key)) {
        weeklyMap.set(key, {
          week: `S${item.weekNumber}`,
          weekNumber: item.weekNumber,
          year: item.year,
          label: `Semaine ${item.weekNumber} (${item.year})`
        });
      }
      weeklyMap.get(key)[item.metric] = item.value;
    });
    
    return Array.from(weeklyMap.values())
      .sort((a, b) => a.year - b.year || a.weekNumber - b.weekNumber);
  }, [analysis.weeklyData, hasWeeklyData]);

  // Données hebdomadaires pour comparaison N vs N-1
  const weeklyComparisonData = useMemo(() => {
    if (!hasWeeklyData) return [];
    
    const currentYearWeeks = weeklyChartData.filter(item => item.year === analysis.kpis.yearCurrent);
    const previousYearWeeks = weeklyChartData.filter(item => item.year === analysis.kpis.yearPrevious);
    
    return currentYearWeeks.map(currentWeek => {
      const previousWeek = previousYearWeeks.find(prev => prev.weekNumber === currentWeek.weekNumber);
      
      return {
        week: currentWeek.week,
        weekNumber: currentWeek.weekNumber,
        [`CA ${analysis.kpis.yearCurrent}`]: currentWeek.CA || 0,
        [`CA ${analysis.kpis.yearPrevious}`]: previousWeek?.CA || 0,
        [`Fréq ${analysis.kpis.yearCurrent}`]: currentWeek.FREQ || 0,
        [`Fréq ${analysis.kpis.yearPrevious}`]: previousWeek?.FREQ || 0,
        [`PM ${analysis.kpis.yearCurrent}`]: currentWeek.PM || 0,
        [`PM ${analysis.kpis.yearPrevious}`]: previousWeek?.PM || 0,
        variationCA: previousWeek?.CA ? 
          (((currentWeek.CA || 0) - (previousWeek.CA || 0)) / (previousWeek.CA || 1) * 100) : 0
      };
    });
  }, [weeklyChartData, hasWeeklyData, analysis.kpis]);

  // Top/Flop semaines
  const topFlopWeeks = useMemo(() => {
    if (!hasWeeklyData) return { top3: [], flop3: [] };
    
    const weeklyPerformance = weeklyComparisonData
      .filter(item => item[`CA ${analysis.kpis.yearCurrent}`] > 0)
      .map(item => ({
        week: item.week,
        weekNumber: item.weekNumber,
        ca: item[`CA ${analysis.kpis.yearCurrent}`],
        variation: item.variationCA
      }))
      .sort((a, b) => b.variation - a.variation);

    return {
      top3: weeklyPerformance.slice(0, 3),
      flop3: weeklyPerformance.slice(-3).reverse()
    };
  }, [weeklyComparisonData, hasWeeklyData, analysis.kpis]);

  // 1. Données pour graphique en aires empilées
  const stackedAreaData = useMemo(() => {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    
    return months.map((month, index) => {
      const caData = analysis.monthlyData.find(item => 
        item.month === month && item.metric === 'CA' && item.year === analysis.kpis.yearCurrent
      );
      const freqData = analysis.monthlyData.find(item => 
        item.month === month && item.metric === 'FREQ' && item.year === analysis.kpis.yearCurrent
      );
      const pmData = analysis.monthlyData.find(item => 
        item.month === month && item.metric === 'PM' && item.year === analysis.kpis.yearCurrent
      );
      
      return {
        month,
        monthNumber: index + 1,
        CA: caData?.value || 0,
        Frequentation: freqData?.value || 0,
        PM: pmData?.value || 0,
        // Données cumulatives
        CA_Cumul: analysis.monthlyData
          .filter(item => item.metric === 'CA' && item.year === analysis.kpis.yearCurrent && item.monthNumber <= index + 1)
          .reduce((sum, item) => sum + item.value, 0),
      };
    });
  }, [analysis.monthlyData, analysis.kpis.yearCurrent]);

  // 2. Données pour camembert répartition mensuelle (basé sur N-1 pour pattern complet)
  const pieChartData = useMemo(() => {
    // Utiliser l'année N-1 pour avoir le pattern saisonnier complet
    const totalCA = analysis.monthlyData
      .filter(item => item.metric === 'CA' && item.year === analysis.kpis.yearPrevious)
      .reduce((sum, item) => sum + item.value, 0);
    
    return analysis.monthlyData
      .filter(item => item.metric === 'CA' && item.year === analysis.kpis.yearPrevious && item.value > 0)
      .map(item => ({
        name: item.month,
        value: item.value,
        percentage: ((item.value / totalCA) * 100).toFixed(1)
      }))
      .sort((a, b) => b.value - a.value);
  }, [analysis.monthlyData, analysis.kpis.yearPrevious]);

  // 3. Données pour graphique radar par trimestre
  const radarData = useMemo(() => {
    const quarters = [
      { name: 'Q1', months: [1, 2, 3] },
      { name: 'Q2', months: [4, 5, 6] },
      { name: 'Q3', months: [7, 8, 9] },
      { name: 'Q4', months: [10, 11, 12] }
    ];

    return quarters.map(quarter => {
      const quarterData = analysis.monthlyData.filter(item => 
        quarter.months.includes(item.monthNumber) && item.year === analysis.kpis.yearCurrent
      );
      
      const caSum = quarterData.filter(item => item.metric === 'CA').reduce((sum, item) => sum + item.value, 0);
      const freqSum = quarterData.filter(item => item.metric === 'FREQ').reduce((sum, item) => sum + item.value, 0);
      const pmAvg = quarterData.filter(item => item.metric === 'PM').reduce((sum, item) => sum + item.value, 0) / 3;
      
      // Objectifs du trimestre
      const objectivesSum = analysis.objectives
        .filter(obj => obj.year === analysis.kpis.yearCurrent && obj.metric === 'CA')
        .filter(obj => {
          const monthName = obj.type;
          const monthNumber = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                              'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].indexOf(monthName) + 1;
          return quarter.months.includes(monthNumber);
        })
        .reduce((sum, obj) => sum + obj.objective, 0);

      return {
        quarter: quarter.name,
        CA: Math.round(caSum / 1000), // En milliers
        Frequentation: Math.round(freqSum / 100), // En centaines
        PM: Math.round(pmAvg),
        Objectifs: Math.round(objectivesSum / 1000), // En milliers
        Performance: Math.round((caSum / objectivesSum) * 100) || 0
      };
    });
  }, [analysis.monthlyData, analysis.objectives, analysis.kpis.yearCurrent]);

  // 4. Données pour graphique waterfall
  const waterfallData = useMemo(() => {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    
    let cumulative = 0;
    return months.map(month => {
      const caData = analysis.monthlyData.find(item => 
        item.month === month && item.metric === 'CA' && item.year === analysis.kpis.yearCurrent
      );
      const objectiveData = analysis.objectives.find(obj => 
        obj.type === month && obj.metric === 'CA' && obj.year === analysis.kpis.yearCurrent
      );
      
      const actual = caData?.value || 0;
      const objective = objectiveData?.objective || 0;
      const variance = actual - objective;
      
      cumulative += variance;
      
      return {
        month,
        actual,
        objective,
        variance,
        cumulative,
        performance: objective > 0 ? ((actual / objective) * 100).toFixed(1) : '0'
      };
    });
  }, [analysis.monthlyData, analysis.objectives, analysis.kpis.yearCurrent]);

  // 5. Calcul du score global sur 100
  const globalScore = useMemo(() => {
    const objectiveScore = parseFloat(analysis.kpis.objectiveRealizationToDate) || 0;
    const growthScore = Math.max(0, Math.min(100, parseFloat(analysis.kpis.revenueGrowthToDate) + 50));
    const consistencyScore = pieChartData.length > 0 ? 
      (100 - (Math.max(...pieChartData.map(d => parseFloat(d.percentage))) - Math.min(...pieChartData.map(d => parseFloat(d.percentage))))) : 50;
    
    return Math.round((objectiveScore * 0.5 + growthScore * 0.3 + consistencyScore * 0.2));
  }, [analysis.kpis, pieChartData]);

  // 6. Top/Flop 3 mois
  const topFlopMonths = useMemo(() => {
    const monthlyPerformance = analysis.monthlyData
      .filter(item => item.metric === 'CA' && item.year === analysis.kpis.yearCurrent)
      .map(item => {
        const objective = analysis.objectives.find(obj => 
          obj.type === item.month && obj.metric === 'CA' && obj.year === analysis.kpis.yearCurrent
        );
        // Calcul de la variation par rapport à l'objectif : ((valeur - objectif) / objectif) * 100
        const variation = objective && objective.objective > 0 ? 
          (((item.value - objective.objective) / objective.objective) * 100) : 0;
        return {
          month: item.month,
          value: item.value,
          performance: variation >= 0 ? `+${variation.toFixed(1)}` : variation.toFixed(1),
          variationValue: variation
        };
      })
      .filter(item => item.value > 0)
      .sort((a, b) => b.variationValue - a.variationValue);

    return {
      top3: monthlyPerformance.slice(0, 3),
      flop3: monthlyPerformance.slice(-3).reverse()
    };
  }, [analysis.monthlyData, analysis.objectives, analysis.kpis.yearCurrent]);

  // 7. Prédictions fin d'année (utilise les données unifiées du backend)
  const yearEndPrediction = useMemo(() => {
    // Utiliser les prédictions calculées par le backend si disponibles
    if (analysis.kpis.yearEndPrediction) {
      return analysis.kpis.yearEndPrediction;
    }
    
    // Fallback vers l'ancien calcul si les nouvelles données ne sont pas disponibles
    const currentMonthData = analysis.monthlyData
      .filter(item => item.metric === 'CA' && item.year === analysis.kpis.yearCurrent && item.value > 0);
    
    if (currentMonthData.length === 0) {
      return { 
        realistic: 0, 
        optimistic: 0, 
        pessimistic: 0, 
        confidence: 0,
        realisticVsObjective: '0',
        methodology: 'Données insuffisantes',
        probabilities: { optimistic: 20, realistic: 60, pessimistic: 20 }
      };
    }
    
    // Calcul simplifié de fallback
    const currentTotal = parseFloat(analysis.kpis.totalRevenueAnnualCurrent);
    const projected = Math.round(currentTotal * (12 / analysis.kpis.currentMonth));
    const caObjective = parseFloat(analysis.kpis.totalCAObjectivesAnnual) || 0;
    const projectedVsObjective = caObjective > 0 ? ((projected / caObjective) * 100) : 0;
    
    return { 
      realistic: projected,
      optimistic: Math.round(projected * 1.15),
      pessimistic: Math.round(projected * 0.85),
      confidence: 60,
      realisticVsObjective: projectedVsObjective.toFixed(1),
      methodology: 'Projection linéaire (fallback)',
      probabilities: { optimistic: 20, realistic: 60, pessimistic: 20 }
    };
  }, [analysis.monthlyData, analysis.kpis]);

  // 8. Données pour heatmap mensuelle
  const heatmapData = useMemo(() => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
                   'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const metrics = ['CA', 'FREQ', 'PM'];
    
    return metrics.map(metric => {
      const monthlyValues = months.map((month, index) => {
        const fullMonth = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][index];
        const data = analysis.monthlyData.find(item => 
          item.month === fullMonth && item.metric === metric && item.year === analysis.kpis.yearCurrent
        );
        const objective = analysis.objectives.find(obj => 
          obj.type === fullMonth && obj.metric === metric && obj.year === analysis.kpis.yearCurrent
        );
        
        // Calcul de la variation par rapport à l'objectif : ((valeur - objectif) / objectif) * 100
        const variation = data && objective && objective.objective > 0 ? 
          (((data.value - objective.objective) / objective.objective) * 100) : 0;
        
        return {
          month,
          value: data?.value || 0,
          performance: Math.round(variation), // Maintenant c'est la variation vs objectif
          color: (!data || data.value === 0) ? '#9CA3AF' : // Gris pour les mois sans données
                 variation >= 0 ? PERFORMANCE_COLORS.excellent : 
                 variation >= -20 ? PERFORMANCE_COLORS.good : PERFORMANCE_COLORS.poor
        };
      });
      
      return {
        metric,
        months: monthlyValues
      };
    });
  }, [analysis.monthlyData, analysis.objectives, analysis.kpis.yearCurrent]);

  // 9. Sparklines data pour les cartes KPI (6 derniers mois de N-1 + mois disponibles de N)
  const sparklineData = useMemo(() => {
    // Prendre les 6 derniers mois de N-1 + les mois disponibles de N
    const months = ['Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre', 
                   'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin'];
    
    return {
      CA: months.map((month, index) => {
        const year = index < 6 ? analysis.kpis.yearPrevious : analysis.kpis.yearCurrent;
        const data = analysis.monthlyData.find(item => 
          item.month === month && item.metric === 'CA' && item.year === year
        );
        return { value: data?.value || 0, month, year };
      }).filter(item => item.value > 0),
      
      FREQ: months.map((month, index) => {
        const year = index < 6 ? analysis.kpis.yearPrevious : analysis.kpis.yearCurrent;
        const data = analysis.monthlyData.find(item => 
          item.month === month && item.metric === 'FREQ' && item.year === year
        );
        return { value: data?.value || 0, month, year };
      }).filter(item => item.value > 0),
      
      PM: months.map((month, index) => {
        const year = index < 6 ? analysis.kpis.yearPrevious : analysis.kpis.yearCurrent;
        const data = analysis.monthlyData.find(item => 
          item.month === month && item.metric === 'PM' && item.year === year
        );
        return { value: data?.value || 0, month, year };
      }).filter(item => item.value > 0)
    };
  }, [analysis.monthlyData, analysis.kpis.yearPrevious, analysis.kpis.yearCurrent]);

  // Calcul optimisé du CA moyen par semaine avec logs
  const weeklyAverageCA = useMemo(() => {
    // Logs détaillés pour le calcul du CA moyen par semaine
    console.log('\n🔍 === CALCUL CA MOYEN PAR SEMAINE (OPTIMISÉ) ===');
    
    // Étape 1: Données brutes
    console.log('📊 Données hebdomadaires brutes:', weeklyChartData.length, 'entrées');
    console.log('📅 Année courante:', analysis.kpis.yearCurrent);
    
    // Étape 2: Filtrage des données de l'année courante
    const currentYearWeeks = weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent);
    console.log('🎯 Semaines année courante:', currentYearWeeks.length);
    console.log('📋 Détail semaines année courante:', currentYearWeeks.map(w => `S${w.weekNumber}: ${w.CA || 0}€`));
    
    // Étape 3: Filtrage des semaines avec CA > 0
    const weeksWithCA = weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0);
    console.log('✅ Semaines avec CA > 0:', weeksWithCA.length);
    console.log('💰 Détail semaines avec CA:', weeksWithCA.map(w => `S${w.weekNumber}: ${w.CA.toLocaleString('fr-FR')}€`));
    
    if (weeksWithCA.length === 0) {
      console.log('❌ Aucune semaine avec CA > 0, retour 0');
      return '0';
    }
    
    // Étape 4: Calcul de la somme
    const totalCA = weeksWithCA.reduce((sum, w) => {
      const ca = w.CA || 0;
      console.log(`   + S${w.weekNumber}: ${ca.toLocaleString('fr-FR')}€`);
      return sum + ca;
    }, 0);
    console.log('💵 SOMME TOTALE CA:', totalCA.toLocaleString('fr-FR'), '€');
    
    // Étape 5: Calcul de la moyenne
    const nbSemaines = weeksWithCA.length;
    const moyenne = totalCA / nbSemaines;
    console.log('📊 CALCUL:', totalCA.toLocaleString('fr-FR'), '€ ÷', nbSemaines, 'semaines =', moyenne.toLocaleString('fr-FR'), '€');
    
    // Étape 6: Arrondi final
    const moyenneArrondie = Math.round(moyenne);
    console.log('🎯 RÉSULTAT FINAL:', moyenneArrondie.toLocaleString('fr-FR'), '€');
    console.log('=== FIN CALCUL CA MOYEN PAR SEMAINE ===\n');
    
    return moyenneArrondie.toLocaleString('fr-FR');
  }, [weeklyChartData, analysis.kpis.yearCurrent]);

  return (
    <div className="space-y-8">
      {/* Navigation des onglets */}
      <div className="flex flex-wrap space-x-2 bg-white rounded-lg shadow-md p-2">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'overview' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          📊 Vue d'ensemble
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'detailed' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          🔍 Analyses détaillées
        </button>
        <button
          onClick={() => setActiveTab('predictive')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'predictive' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          🚀 Prédictif & IA
        </button>
        <button
          onClick={() => setActiveTab('weekly')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'weekly' 
              ? 'bg-blue-600 text-white' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          📅 Analyses hebdomadaires
        </button>
      </div>

      {/* Onglet Vue d'ensemble */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Score global et Gauge */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div 
              className="analysis-section"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
            >
              <div className="analysis-title">
                <CpuChipIcon className="h-6 w-6 text-blue-600 mr-2" />
                Score Global de Performance
              </div>
              <div className="flex flex-col items-center space-y-4">
                <div className="text-6xl font-bold text-blue-600">{globalScore}/100</div>
                <div className="text-lg text-gray-600">
                  {globalScore >= 80 ? '🏆 Excellente performance' :
                   globalScore >= 60 ? '👍 Bonne performance' :
                   globalScore >= 40 ? '⚠️ Performance moyenne' : '🚨 Performance à améliorer'}
                </div>
                <div className="w-full max-w-xs">
                  <GaugeChart 
                    id="performance-gauge"
                    nrOfLevels={3}
                    colors={['#EF4444', '#F59E0B', '#10B981']}
                    percent={globalScore / 100}
                    formatTextValue={() => ''}
                  />
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="analysis-section"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="analysis-title">
                <TrophyIcon className="h-6 w-6 text-blue-600 mr-2" />
                Top/Flop Performances
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-green-600 mb-2">🏆 Top 3 Mois</h4>
                  {topFlopMonths.top3.map((month, index) => (
                    <div key={index} className="flex justify-between items-center py-1">
                      <span className="text-sm">{month.month}</span>
                      <span className={`text-sm font-medium ${month.variationValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {month.performance}%
                      </span>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="font-semibold text-red-600 mb-2">📉 Flop 3 Mois</h4>
                  {topFlopMonths.flop3.map((month, index) => (
                    <div key={index} className="flex justify-between items-center py-1">
                      <span className="text-sm">{month.month}</span>
                      <span className={`text-sm font-medium ${month.variationValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {month.performance}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Graphique en aires empilées */}
          <motion.div 
            className="analysis-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="analysis-title">
              <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
              Évolution Cumulative Multi-Métriques
            </div>
            <div className="chart-container" style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stackedAreaData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      typeof name === 'string' && name.includes('CA') ? `${value.toLocaleString('fr-FR')} €` :
                      typeof name === 'string' && name.includes('Frequentation') ? `${value.toLocaleString('fr-FR')} visiteurs` :
                      `${value.toLocaleString('fr-FR')} €`,
                      name
                    ]}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="CA" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} />
                  <Area type="monotone" dataKey="Frequentation" stackId="2" stroke="#10B981" fill="#10B981" fillOpacity={0.6} />
                  <Area type="monotone" dataKey="PM" stackId="3" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      )}

      {/* Onglet Analyses détaillées */}
      {activeTab === 'detailed' && (
        <div className="space-y-8">
          {/* Heatmap mensuelle */}
          <motion.div 
            className="analysis-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="analysis-title">
              <FireIcon className="h-6 w-6 text-blue-600 mr-2" />
              Heatmap Variation vs Objectifs Mensuels
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {heatmapData.map((metricData, metricIndex) => (
                  <div key={metricIndex} className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-2">{metricData.metric}</h4>
                    <div className="grid grid-cols-12 gap-2">
                      {metricData.months.map((monthData, monthIndex) => (
                        <div
                          key={monthIndex}
                          className="aspect-square rounded-lg flex flex-col items-center justify-center text-white text-xs font-medium relative group"
                          style={{ backgroundColor: monthData.color }}
                        >
                          <div>{monthData.month}</div>
                          <div>{monthData.performance}%</div>
                          
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                            {typeof monthData.value === 'number' ? monthData.value.toLocaleString('fr-FR') : monthData.value} {typeof metricData.metric === 'string' && metricData.metric.includes('CA') ? '€' : typeof metricData.metric === 'string' && metricData.metric.includes('FREQ') ? 'visiteurs' : '€'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: PERFORMANCE_COLORS.excellent }}></div>
                <span>≥0% vs objectif</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: PERFORMANCE_COLORS.good }}></div>
                <span>-20% à -1% vs objectif</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: PERFORMANCE_COLORS.poor }}></div>
                <span>&lt;-20% vs objectif</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9CA3AF' }}></div>
                <span>Aucune donnée</span>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Onglet Prédictif & IA */}
      {activeTab === 'predictive' && (
        <div className="space-y-8">
          {/* Prédictions fin d'année */}
          <motion.div 
            className="analysis-section"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="analysis-title">
              <SparklesIcon className="h-6 w-6 text-blue-600 mr-2" />
              Prédictions Intelligentes Fin d'Année
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`rounded-lg p-6 border ${
                yearEndPrediction.confidence >= 80 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 
                yearEndPrediction.confidence >= 60 ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200' : 
                'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
              }`}>
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {yearEndPrediction.realistic.toLocaleString('fr-FR')} €
                </div>
                <div className="text-sm text-gray-600">CA Prédit Fin 2025</div>
                <div className="mt-2 text-xs text-blue-600">
                  Scénario réaliste (60%)
                </div>
              </div>
              
              <div className={`rounded-lg p-6 border ${
                yearEndPrediction.confidence >= 80 ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 
                yearEndPrediction.confidence >= 60 ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200' : 
                'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
              }`}>
                <div className={`text-3xl font-bold mb-2 ${
                  yearEndPrediction.confidence >= 80 ? 'text-green-600' : 
                  yearEndPrediction.confidence >= 60 ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>
                  {yearEndPrediction.confidence}%
                </div>
                <div className="text-sm text-gray-600 flex items-center relative group">
                  Niveau de Confiance
                  <svg 
                    className="w-4 h-4 ml-1 text-gray-400 cursor-help" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  {/* Tooltip explicatif */}
                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-20 whitespace-nowrap max-w-xs">
                    <div className="font-semibold mb-1">💡 Calcul du Niveau de Confiance :</div>
                    <div className="space-y-1 text-left">
                      <div>• <strong>Régularité :</strong> Stabilité des performances mensuelles</div>
                      <div>• <strong>Tendance :</strong> Cohérence de la croissance/décroissance</div>
                      <div>• <strong>Données :</strong> Nombre de mois complets disponibles</div>
                      <div>• <strong>Saisonnalité :</strong> Prévisibilité des patterns historiques</div>
                      <div className="pt-1 border-t border-gray-600 mt-2">
                        <span className="text-green-300">≥80% = Très fiable</span> | 
                        <span className="text-yellow-300">60-79% = Fiable</span> | 
                        <span className="text-red-300">&lt;60% = Modéré</span>
                      </div>
                    </div>
                    {/* Flèche du tooltip */}
                    <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
                  </div>
                </div>
                <div className={`mt-2 text-xs ${
                  yearEndPrediction.confidence >= 80 ? 'text-green-600' : 
                  yearEndPrediction.confidence >= 60 ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>
                  {yearEndPrediction.confidence >= 80 ? 'Très fiable' :
                   yearEndPrediction.confidence >= 60 ? 'Fiable' : 'Modéré'}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-6 border border-purple-200">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {yearEndPrediction.realisticVsObjective}%
                </div>
                <div className="text-sm text-gray-600">Réalisation Objectif Prédite</div>
                <div className="mt-2 text-xs text-purple-600">
                  Projection vs objectif annuel
                </div>
              </div>
            </div>
            
            {/* Nouveaux scénarios détaillés */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                <div className="text-lg font-bold text-green-600 mb-1">
                  {yearEndPrediction.optimistic.toLocaleString('fr-FR')} €
                </div>
                <div className="text-sm text-gray-600">Scénario Optimiste (+15%)</div>
                <div className="text-xs text-green-600 mt-1">
                  Probabilité : {yearEndPrediction.probabilities.optimistic}%
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <div className="text-lg font-bold text-blue-600 mb-1">
                  {yearEndPrediction.realistic.toLocaleString('fr-FR')} €
                </div>
                <div className="text-sm text-gray-600">Scénario Réaliste</div>
                <div className="text-xs text-blue-600 mt-1">
                  Probabilité : {yearEndPrediction.probabilities.realistic}%
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-4 border border-red-200">
                <div className="text-lg font-bold text-red-600 mb-1">
                  {yearEndPrediction.pessimistic.toLocaleString('fr-FR')} €
                </div>
                <div className="text-sm text-gray-600">Scénario Pessimiste (-15%)</div>
                <div className="text-xs text-red-600 mt-1">
                  Probabilité : {yearEndPrediction.probabilities.pessimistic}%
                </div>
              </div>
            </div>
            
            {/* Méthodologie */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-700">
                <strong>Méthodologie :</strong> {yearEndPrediction.methodology}
              </div>
            </div>
          </motion.div>

          {/* Sparklines dans les métriques */}
          <motion.div 
            className="analysis-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="analysis-title">
              <BoltIcon className="h-6 w-6 text-blue-600 mr-2" />
              Tendances Micro-Graphiques
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(sparklineData).map(([metric, data], index) => (
                <div key={metric} className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-800">
                      {metric === 'CA' ? 'Chiffre d\'Affaires' :
                       metric === 'FREQ' ? 'Fréquentation' : 'Panier Moyen'}
                    </h4>
                    <div className="text-sm text-gray-500">6 derniers mois</div>
                  </div>
                  <div style={{ height: '60px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data}>
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke={COLORS[index]} 
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    Tendance: {data[data.length - 1]?.value > data[0]?.value ? '📈 Hausse' : '📉 Baisse'}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Analyse saisonnière */}
          <motion.div 
            className="analysis-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="analysis-title">
              <CalendarIcon className="h-6 w-6 text-blue-600 mr-2" />
              Détection de Patterns Saisonniers (basé sur {analysis.kpis.yearPrevious})
            </div>
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Méthodologie :</strong> L'analyse saisonnière utilise les données complètes de {analysis.kpis.yearPrevious} 
                 pour identifier les patterns et prédire les performances des mois restants de {analysis.kpis.yearCurrent}. 
                Les prédictions appliquent les coefficients saisonniers de {analysis.kpis.yearPrevious} à la tendance actuelle.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800">Mois de Forte Activité ({analysis.kpis.yearPrevious})</h4>
                {pieChartData.slice(0, 3).map((month, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="font-medium">{month.name}</span>
                    <span className="text-green-600 font-bold">{month.percentage}%</span>
                  </div>
                ))}
                <div className="text-xs text-gray-600 mt-2">
                  Ces mois représentent les pics d'activité historiques
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800">Mois de Faible Activité ({analysis.kpis.yearPrevious})</h4>
                {pieChartData.slice(-3).map((month, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <span className="font-medium">{month.name}</span>
                    <span className="text-red-600 font-bold">{month.percentage}%</span>
                  </div>
                ))}
                <div className="text-xs text-gray-600 mt-2">
                  Ces mois nécessitent une attention particulière
                </div>
              </div>
            </div>
            
            {/* Insights saisonniers */}
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-2">💡 Insights Saisonniers</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-purple-700">Pic principal :</span>
                  <span className="text-purple-600 ml-2">{pieChartData[0]?.name} ({pieChartData[0]?.percentage}%)</span>
                </div>
                <div>
                  <span className="font-medium text-purple-700">Mois le plus faible :</span>
                  <span className="text-purple-600 ml-2">{pieChartData[pieChartData.length - 1]?.name} ({pieChartData[pieChartData.length - 1]?.percentage}%)</span>
                </div>
                <div>
                  <span className="font-medium text-purple-700">Écart max/min :</span>
                  <span className="text-purple-600 ml-2">
                    {(parseFloat(pieChartData[0]?.percentage || '0') - parseFloat(pieChartData[pieChartData.length - 1]?.percentage || '0')).toFixed(1)} points
                  </span>
                </div>
                <div>
                  <span className="font-medium text-purple-700">Saisonnalité :</span>
                  <span className="text-purple-600 ml-2">
                    {(parseFloat(pieChartData[0]?.percentage || '0') - parseFloat(pieChartData[pieChartData.length - 1]?.percentage || '0')) > 15 ? 'Forte' : 'Modérée'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Onglet Analyses hebdomadaires */}
      {activeTab === 'weekly' && (
        <div className="space-y-8">
          {!hasWeeklyData ? (
            <motion.div 
              className="analysis-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="text-center py-8">
                <CalendarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Données hebdomadaires non disponibles</h3>
                <p className="text-gray-500">
                  Aucune feuille de suivi hebdomadaire n'a été détectée dans votre fichier Excel.
                  <br />
                  Assurez-vous d'avoir une feuille nommée "suivi hebdo" ou "hebdomadaire".
                </p>
              </div>
            </motion.div>
          ) : (
            <>
              {/* Statistiques hebdomadaires */}
              <motion.div 
                className="analysis-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="analysis-title">
                  <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
                  Statistiques Hebdomadaires
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div className="text-2xl font-bold text-blue-600">
                      {weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0).length}
                    </div>
                    <div className="text-sm text-gray-600">Semaines avec données {analysis.kpis.yearCurrent}</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
                    <div className="text-2xl font-bold text-purple-600">
                      {weeklyAverageCA} €
                    </div>
                    <div className="text-sm text-gray-600">CA moyen par semaine</div>
                  </div>
                </div>
              </motion.div>

              {/* Graphique évolution hebdomadaire CA */}
              <motion.div 
                className="analysis-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="analysis-title">
                  <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
                  Évolution Hebdomadaire du Chiffre d'Affaires
                </div>
                <div style={{ height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={weeklyComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip 
                        formatter={(value, name) => [
                          typeof value === 'number' ? value.toLocaleString('fr-FR') + (typeof name === 'string' && name.includes('CA') ? ' €' : typeof name === 'string' && name.includes('Fréq') ? '' : '%') : value,
                          name
                        ]}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey={`CA ${analysis.kpis.yearPrevious}`} fill="#94A3B8" name={`CA ${analysis.kpis.yearPrevious}`} />
                      <Bar yAxisId="left" dataKey={`CA ${analysis.kpis.yearCurrent}`} fill="#3B82F6" name={`CA ${analysis.kpis.yearCurrent}`} />
                      <Line yAxisId="right" type="monotone" dataKey="variationCA" stroke="#EF4444" strokeWidth={2} name="Évolution %" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Graphique fréquentation hebdomadaire */}
              <motion.div 
                className="analysis-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="analysis-title">
                  <EyeIcon className="h-6 w-6 text-blue-600 mr-2" />
                  Évolution Hebdomadaire de la Fréquentation
                </div>
                <div style={{ height: '350px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={weeklyComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [
                          typeof value === 'number' ? value.toLocaleString('fr-FR') : value,
                          name
                        ]}
                      />
                      <Legend />
                      <Line type="monotone" dataKey={`Fréq ${analysis.kpis.yearPrevious}`} stroke="#94A3B8" strokeWidth={2} name={`Fréquentation ${analysis.kpis.yearPrevious}`} />
                      <Line type="monotone" dataKey={`Fréq ${analysis.kpis.yearCurrent}`} stroke="#10B981" strokeWidth={2} name={`Fréquentation ${analysis.kpis.yearCurrent}`} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Graphique panier moyen hebdomadaire */}
              <motion.div 
                className="analysis-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="analysis-title">
                  <SparklesIcon className="h-6 w-6 text-blue-600 mr-2" />
                  Évolution Hebdomadaire du Panier Moyen
                </div>
                <div style={{ height: '350px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={weeklyComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => [
                          typeof value === 'number' ? value.toFixed(2) + ' €' : value,
                          name
                        ]}
                      />
                      <Legend />
                      <Area type="monotone" dataKey={`PM ${analysis.kpis.yearPrevious}`} stackId="1" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.3} name={`PM ${analysis.kpis.yearPrevious}`} />
                      <Area type="monotone" dataKey={`PM ${analysis.kpis.yearCurrent}`} stackId="2" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} name={`PM ${analysis.kpis.yearCurrent}`} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Top/Flop semaines */}
              <motion.div 
                className="analysis-section"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div className="analysis-title">
                  <TrophyIcon className="h-6 w-6 text-blue-600 mr-2" />
                  Performance Hebdomadaire - Top/Flop
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
                    <h4 className="font-semibold text-green-700 mb-4 flex items-center">
                      🏆 Top 3 Semaines (vs {analysis.kpis.yearPrevious})
                    </h4>
                    {topFlopWeeks.top3.map((week, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-green-100 last:border-b-0">
                        <div>
                          <span className="font-medium text-green-800">{week.week}</span>
                          <div className="text-sm text-green-600">{week.ca.toLocaleString('fr-FR')} €</div>
                        </div>
                        <span className="text-lg font-bold text-green-600">
                          +{week.variation.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-6 border border-red-200">
                    <h4 className="font-semibold text-red-700 mb-4 flex items-center">
                      📉 Flop 3 Semaines (vs {analysis.kpis.yearPrevious})
                    </h4>
                    {topFlopWeeks.flop3.map((week, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-red-100 last:border-b-0">
                        <div>
                          <span className="font-medium text-red-800">{week.week}</span>
                          <div className="text-sm text-red-600">{week.ca.toLocaleString('fr-FR')} €</div>
                        </div>
                        <span className="text-lg font-bold text-red-600">
                          {week.variation.toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Insights hebdomadaires */}
              <motion.div 
                className="analysis-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="analysis-title">
                  <CpuChipIcon className="h-6 w-6 text-blue-600 mr-2" />
                  Insights Hebdomadaires
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <h4 className="font-semibold text-blue-700 mb-3">📊 Analyse de Volatilité</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Écart-type CA hebdomadaire :</span>
                        <span className="font-medium">
                          {weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0).length > 0 ? 
                            Math.round(Math.sqrt(weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0)
                              .reduce((sum, w, _, arr) => {
                                const mean = arr.reduce((s, item) => s + (item.CA || 0), 0) / arr.length;
                                return sum + Math.pow((w.CA || 0) - mean, 2);
                              }, 0) / weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0).length)).toLocaleString('fr-FR') : 0} €
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Coefficient de variation :</span>
                        <span className="font-medium">
                          {weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0).length > 0 ? 
                            ((Math.sqrt(weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0)
                              .reduce((sum, w, _, arr) => {
                                const mean = arr.reduce((s, item) => s + (item.CA || 0), 0) / arr.length;
                                return sum + Math.pow((w.CA || 0) - mean, 2);
                              }, 0) / weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0).length) /
                              (weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0).reduce((sum, w) => sum + (w.CA || 0), 0) / 
                              weeklyChartData.filter(w => w.year === analysis.kpis.yearCurrent && w.CA > 0).length)) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-6 border border-purple-200">
                    <h4 className="font-semibold text-purple-700 mb-3">🎯 Tendances Détectées</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="text-purple-600 mr-2">•</span>
                        <span>
                          {weeklyComparisonData.filter(w => w.variationCA > 0).length > weeklyComparisonData.filter(w => w.variationCA < 0).length ? 
                            'Tendance majoritairement positive' : 'Tendance majoritairement négative'}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-purple-600 mr-2">•</span>
                        <span>
                          {weeklyComparisonData.filter(w => w.variationCA > 0).length} semaines en hausse, {weeklyComparisonData.filter(w => w.variationCA < 0).length} en baisse
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-purple-600 mr-2">•</span>
                        <span>
                          Meilleure performance : {topFlopWeeks.top3[0]?.week} (+{topFlopWeeks.top3[0]?.variation.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedCharts; 